// src/components/layout/ApplyModal.jsx
import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';

const ApplyModal = ({ isOpen, onClose, collegeName = "General", source = "unknown" }) => {
  const formRef = useRef(null);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  // STRICT VALIDATION FUNCTION
  const validateForm = () => {
    const newErrors = {};
    const formData = new FormData(formRef.current);
    
    // Name validation - only letters and spaces
    const name = formData.get('name')?.trim();
    if (!name || name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(name)) {
      newErrors.name = 'Name can only contain letters';
    }

    // Email validation
    const email = formData.get('email')?.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation - exactly 10 digits
    if (phone.length !== 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    // City validation - only letters
    const city = formData.get('city')?.trim();
    if (!city || city.length < 2) {
      newErrors.city = 'City must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(city)) {
      newErrors.city = 'City can only contain letters';
    }

    // Course validation
    const course = formData.get('course');
    if (!course || course === '') {
      newErrors.course = 'Please select a course';
    }

    // Qualification validation
    const qualification = formData.get('qualification')?.trim();
    if (!qualification || qualification.length < 2) {
      newErrors.qualification = 'Please enter your qualification';
    }

    // Marks validation
    const marks = formData.get('marks')?.trim();
    if (!marks) {
      newErrors.marks = 'Please enter your marks/CGPA';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      const errorField = document.getElementById(firstError);
      if (errorField) {
        errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorField.focus();
      }
      return;
    }

    setLoading(true);

    // Prepare email data with tracking
    const formData = new FormData(formRef.current);
    const templateParams = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      city: formData.get('city'),
      course: formData.get('course'),
      qualification: formData.get('qualification'),
      marks: formData.get('marks'),
      message: formData.get('message') || 'No message provided',
      // TRACKING FIELDS
      college_name: collegeName,
      form_source: source,
      page_url: window.location.href,
      submission_time: new Date().toLocaleString('en-IN', { 
        timeZone: 'Asia/Kolkata',
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    };

    // Send via EmailJS
    emailjs
      .send(
        'service_u0g0bqj',
        'template_naso7ph',
        templateParams,
        '0JNndpSn5KZm_5yLJ'
      )
      .then(
        () => {
          setLoading(false);
          setShowSuccess(true);
          formRef.current.reset();
          setPhone('');
          setErrors({});
          setTimeout(() => {
            setShowSuccess(false);
            onClose();
          }, 3000);
        },
        (error) => {
          setLoading(false);
          console.error('EmailJS error:', error);
          alert('Failed to submit. Please check your internet connection and try again.');
        }
      );
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-4 flex items-center justify-between">
          <h3 className="text-2xl font-bold text-white">
            Apply Now {collegeName !== "General" && `- ${collegeName}`}
          </h3>
          <button
            type="button"
            className="text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center text-2xl transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Form Area */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
          {!showSuccess ? (
            <form
              ref={formRef}
              noValidate
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Full Name */}
                <div className="md:col-span-2">
                  <label 
                    htmlFor="name" 
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                  >
                    Full Name *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="Enter your full name (letters only)"
                    className={`w-full bg-slate-100 dark:bg-slate-700 border ${
                      errors.name ? 'border-red-500' : 'border-transparent'
                    } focus:border-sky-500 focus:ring-2 focus:ring-sky-500 rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 transition-all duration-200`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1 font-medium">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label 
                    htmlFor="email" 
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="john@example.com"
                    className={`w-full bg-slate-100 dark:bg-slate-700 border ${
                      errors.email ? 'border-red-500' : 'border-transparent'
                    } focus:border-sky-500 focus:ring-2 focus:ring-sky-500 rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 transition-all duration-200`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1 font-medium">{errors.email}</p>
                  )}
                </div>

                {/* Mobile Number */}
                <div>
                  <label 
                    htmlFor="phone" 
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                  >
                    Mobile Number *
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={phone}
                    required
                    placeholder="10-digit number"
                    maxLength="10"
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setPhone(digits);
                      setErrors({ ...errors, phone: '' });
                    }}
                    className={`w-full bg-slate-100 dark:bg-slate-700 border ${
                      errors.phone ? 'border-red-500' : 'border-transparent'
                    } focus:border-sky-500 focus:ring-2 focus:ring-sky-500 rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 transition-all duration-200`}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1 font-medium">{errors.phone}</p>
                  )}
                </div>

                {/* City */}
                <div>
                  <label 
                    htmlFor="city" 
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                  >
                    City *
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    required
                    placeholder="Your city (letters only)"
                    className={`w-full bg-slate-100 dark:bg-slate-700 border ${
                      errors.city ? 'border-red-500' : 'border-transparent'
                    } focus:border-sky-500 focus:ring-2 focus:ring-sky-500 rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 transition-all duration-200`}
                  />
                  {errors.city && (
                    <p className="text-red-500 text-sm mt-1 font-medium">{errors.city}</p>
                  )}
                </div>

                {/* Interested Course */}
                <div>
                  <label 
                    htmlFor="course" 
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                  >
                    Interested Course *
                  </label>
                  <select
                    id="course"
                    name="course"
                    required
                    className={`w-full bg-slate-100 dark:bg-slate-700 border ${
                      errors.course ? 'border-red-500' : 'border-transparent'
                    } focus:border-sky-500 focus:ring-2 focus:ring-sky-500 rounded-xl px-4 py-3 text-slate-900 dark:text-white appearance-none transition-all duration-200 cursor-pointer`}
                  >
                    <option value="">- Select a course -</option>
                    <option value="btech-cse">B.Tech CSE</option>
                    <option value="bba">BBA</option>
                    <option value="bca">BCA</option>
                    <option value="mba">MBA</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.course && (
                    <p className="text-red-500 text-sm mt-1 font-medium">{errors.course}</p>
                  )}
                </div>

                {/* Current Qualification */}
                <div>
                  <label 
                    htmlFor="qualification" 
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                  >
                    Current Qualification *
                  </label>
                  <input
                    id="qualification"
                    name="qualification"
                    type="text"
                    required
                    placeholder="e.g., 12th Pass, Graduate"
                    className={`w-full bg-slate-100 dark:bg-slate-700 border ${
                      errors.qualification ? 'border-red-500' : 'border-transparent'
                    } focus:border-sky-500 focus:ring-2 focus:ring-sky-500 rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 transition-all duration-200`}
                  />
                  {errors.qualification && (
                    <p className="text-red-500 text-sm mt-1 font-medium">{errors.qualification}</p>
                  )}
                </div>

                {/* Percentage/CGPA */}
                <div>
                  <label 
                    htmlFor="marks" 
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                  >
                    Approx. Percentage / CGPA *
                  </label>
                  <input
                    id="marks"
                    name="marks"
                    type="text"
                    required
                    placeholder="e.g., 85% or 8.5"
                    className={`w-full bg-slate-100 dark:bg-slate-700 border ${
                      errors.marks ? 'border-red-500' : 'border-transparent'
                    } focus:border-sky-500 focus:ring-2 focus:ring-sky-500 rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 transition-all duration-200`}
                  />
                  {errors.marks && (
                    <p className="text-red-500 text-sm mt-1 font-medium">{errors.marks}</p>
                  )}
                </div>

                {/* Message */}
                <div className="md:col-span-2">
                  <label 
                    htmlFor="message" 
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                  >
                    Message / Query (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="3"
                    placeholder="Any questions or preferences?"
                    className="w-full bg-slate-100 dark:bg-slate-700 border-transparent focus:border-sky-500 focus:ring-2 focus:ring-sky-500 rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 transition-all duration-200 resize-none"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-bold py-3.5 px-8 rounded-xl shadow-lg transform transition hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-sky-300 flex items-center justify-center gap-2 text-lg disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <span>→</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            // Success Message
            <div className="text-center py-12 px-6">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-green-500 text-5xl font-bold">✓</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                Application Submitted!
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-lg">
                Thank you for your application. Our team will contact you shortly.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplyModal;
